import axios from 'axios'

const axiosInstance = axios.create({
    baseURL: process.env.VUE_APP_API_URL,
    timeout: 30000,
    headers: {
        'Content-Type': 'application/json'
    },
    withCredentials: true
})

// Обработка ошибок
axiosInstance.interceptors.response.use(
    (response) => response.data,
    (error) => {
        if(error.response) {
            console.error('Ошибка ответа от сервера:', error.response.status, error.response.data)
        } else if(error.request) {
            console.error('Ошибка запроса:', error.request)
        } else {
            console.error('Ошибка настройки запроса:', error.message)
        }

        return Promise.reject(error) // Возвращаем ошибку
    }
)

// ==============================
// Accounts
// ==============================

const getCurrentBalance = () => {
    return axiosInstance.get('/accounts/my')
}

const getCurrentBonusBalance = () => {
    return axiosInstance.get('/bonus-accounts/my')
}

// ==============================
// Claims
// ==============================

const checkClaims = (data) => {
    return axiosInstance.post('/claims/check-claims', data)
}

const checkNoticesHome = (data) => {
    return axiosInstance.post('/debt-notice/check-notices-home', data)
}

const getClaims = (params = {}) => {
    return axiosInstance.get('/claims', { params })
}

const getClaimById = (claimId, params = {}) => {
    return axiosInstance.get('/claims/' + claimId, { params })
}

const getClaimPreview = (slug) => {
    return axiosInstance.get(`/claims/${slug}/preview`)
}

const getNotificationMethods = (paid = false) => {
    return axiosInstance.get('/claims/notification-methods', { params: { paid } })
}

const getUnreadClaim = () => {
    return axiosInstance.get(`/claims/get-unreaded`)
}

const readClaim = (claimId) => {
    return axiosInstance.get(`/claims/${claimId}/read`)
}

const getClaimCost = () => {
    return axiosInstance.get('/claims/cost')
}

const getUnreadDebtNotice = () => {
    return axiosInstance.get(`/debt-notice/get-unread`)
}

const readNotice = (noticeId) => {
    return axiosInstance.get(`/debt-notice/${noticeId}/read`)
}

const deleteDebtNotice = (noticeId) => {
    return axiosInstance.post('/debt-notice/delete', { notice_id: noticeId })
}

const checkNotices = () => {
    return axiosInstance.get('/debt-notice/check-notices')
}

const checkNoticeStatuses = () => {
    return axiosInstance.get('/debt-notice/check-statuses')
}

const setNoticeSenderNotified = (noticeId) => {
    return axiosInstance.post('/debt-notice/sender-notified', { notice_id: noticeId })
}

const checkAddressRecipient = (address) => {
    return axiosInstance.post(`/debt-notice/check-address`, { address: address })
}

const addAddressVisibility = (addressId, userId) => {
    return axiosInstance.post('/debt-notice/add-address-visibility', { addressId, userId })
}

const addRecipientRelation = (recipientId) => {
    return axiosInstance.post('/recipients/relations', { recipientId })
}

const checkOsiStatus = () => {
    return axiosInstance.get('/debt-notice/check-osi-status')
}

const importDebtNotices = (params) => {
    return axiosInstance.get('/debt-notice/check-imported-notices', { params })
}

const uploadDebtNotices = (body) => {
    return axiosInstance.post('/debt-notice/upload-import', body)
}

const getNoticeSenderData = (sender) => {
    return axiosInstance.post('/debt-notice/sender-data', { sender: sender })
}
const getSenderData = (userId) => {
    return axiosInstance.post('/debt-notice/sender-data-user-id', { userId: userId })
}

const addressRecipients = (addressId) => {
    return axiosInstance.post('/debt-notice/address-recipients', { addressId: addressId })
}

const getContactsLimits = () => {
    return axiosInstance.get('/claims/contacts-limits')
}

const sendClaimNotifications = (slug, method, contacts, bonusAccountUsage) => {
    return axiosInstance.post(`/claims/${slug}/send-notifications`, { method, contacts, bonusAccountUsage })
}

// ==============================
// Otp codes
// ==============================

const otpSendCode = (target) => {
    return axiosInstance.post('/signs/otp/send-code', { target })
}

const otpApproveCode = (sessionId, code) => {
    return axiosInstance.post('/signs/otp/approve-code', { sessionId, code })
}

// ==============================
// Messages
// ==============================

const getMessages = (slug) => {
    return axiosInstance.get(`/claims/${slug}/messages`)
}

const addMessage = (slug, data) => {
    const config = {
        headers: { 'Content-Type': 'multipart/form-data' }
    }

    return axiosInstance.post(`/claims/${slug}/add-message`, data, config)
}

const messageAction = (slug, data) => {
    const config = {
        headers: { 'Content-Type': 'multipart/form-data' }
    }

    return axiosInstance.post(`/claims/${slug}/message-action`, data, config)
}

const doMessageAction = (slug, data) => {
    const config = {
        headers: { 'Content-Type': 'multipart/form-data' }
    }

    return axiosInstance.post(`/claims/${slug}/message-action/use-data`, data, config)
}

const getMessageDataForSign = (slug, data) => {
    const config = {
        headers: { 'Content-Type': 'multipart/form-data' }
    }

    return axiosInstance.post(`/claims/${slug}/message-action/get-data`, data, config)
}

// ==============================
// Rates
// ==============================

const getRates = () => {
    return axiosInstance.get(`/rates`)
}

// ==============================
// Recipients
// ==============================

const getRecipient = (recipientId) => {
    return axiosInstance.get(`/recipients/${recipientId}`)
}

const findRecipientByFields = (fields, values) => {
    return axiosInstance.post('/recipients/by-fields', { fields, values })
}

const linkRecipientAddress = (recipientId, address) => {
    return axiosInstance.post('/recipients/address-link', { recipientId, address })
}
const updateRecipientContact = (recipientId, key, value) => {
    return axiosInstance.post(`/recipients/contact/update`, { recipientId, key, value })
}

const removeRecipentContact = (key, contactId) => {
    return axiosInstance.post('/recipients/contact/remove', { key, contactId })
}

const getRecipentAddress = (recipientId) => {
    return axiosInstance.get(`/recipients/${recipientId}/address`)
}

// ==============================
// Transactions
// ==============================

const getTransactionStatus = (id) => {
    return axiosInstance.get(`/transactions/status/${id}`)
}

const replenishAccount = (amount, paymentService) => {
    return axiosInstance.get('/transactions/replenish-account', { params: { amount, paymentService } })
}

const claimPayment = (slug) => {
    return axiosInstance.get(`/claims/${slug}/payment`)
}

const noticePayment = (slug) => {
    return axiosInstance.get(`/debt-notice/${slug}/payment`)
}

const getReceipt = (slug) => {
    return axiosInstance.get(`/receipts/${slug}`)
}

const getBills = () => {
    return axiosInstance.get(`/transactions/get-bills`)
}

const requestBill = (amount) => {
    return axiosInstance.get(`/transactions/request-bill`, { params: { amount } })
}

// ==============================
// Location
// ==============================

const getCountries = () => {
    return axiosInstance.get('/location/countries')
}

const getRegions = (countryId) => {
    return axiosInstance.get('/location/regions', { params: { countryId } })
}

const getLocalities = (regionId) => {
    return axiosInstance.get('/location/localities', { params: { regionId } })
}

const getSubLocalities = (localityId) => {
    return axiosInstance.get('/location/sub-localities', { params: { localityId } })
}

const getStreets = (localityId) => {
    return axiosInstance.get('/location/streets', { params: { localityId } })
}

const addStreet = (localityId, title) => {
    return axiosInstance.post('/location/streets', { localityId, title })
}

const getRkaAddresses = (address) => {
    if(!address) return false
    if(address.length < 3) return false
    return axiosInstance.get('/location/rka-address', { params: { address } })
}

const getCities = (filter) => {
    return axiosInstance.get('/location/rka-cities', { params: { filter } })
}

const getRKAStreets = (city, filter = '') => {
    return axiosInstance.get('/location/rka-streets', { params: { city, filter }})
}

const getRKABuildings = (city, street, filter = '') => {
    return axiosInstance.get('/location/rka-buildings', { params: { city, street, filter }})
}

const getRKABuildingParts = (building, filter) => {
    return axiosInstance.get('/location/rka-building-parts', { params: { building, filter }})
}

const getRKAAddress = (city, street, building, part) => {
    return axiosInstance.get('/location/rka-full-address', { params: { city, street, building, part }})
}

// #region GET ALL RKA

const getAllCities = (filter) => {
    return axiosInstance.get('/location/rka-all-cities', { params: { filter } })
}

const getAllRKAStreets = (city, filter = '') => {
    return axiosInstance.get('/location/rka-all-streets', { params: { city, filter }})
}

const getAllRKABuildings = (city, street) => {
    return axiosInstance.get('/location/rka-all-buildings', { params: { city, street }})
}

const getAllRKABuildingParts = (building) => {
    return axiosInstance.get('/location/rka-all-building-parts', { params: { building }})
}
// #endregion

const getAddressesNumber = (archived) => {
    return axiosInstance.get('/location/addresses-number', { params: { archived } })
}

const getFirstAddress = (archived, addressId = null) => {
    return axiosInstance.get('/location/first-address', { params: { archived, addressId } })
}

const findCityByKato = (kato, cityName) => {
    return axiosInstance.get('/location/find-city-kato', { params: { kato, cityName }})
}

const findStreetByName = (city, name) => {
    return axiosInstance.get('/location/find-street', { params: { city, name }})
}

const saveMigratedAddress = (address) => {
    return axiosInstance.post('/location/save-migrated', { address })
}

const setHouseByNumber = (obj) => {
    return axiosInstance.post('/location/set-house', { obj })
}

const setAptByNumber = (obj) => {
    return axiosInstance.post('/location/set-apt', { obj })
}

const deleteAddress = (id) => {
    return axiosInstance.post('/location/delete-address', { id })
}

const archive = (id) => {
    return axiosInstance.post('/location/archive-address', { id })
}

const filterBdg = (city, str, building) => {
    return axiosInstance.get('/location/filter-bdg', { params: { city, str, building } })
}

const filterPb = (building, part) => {
    return axiosInstance.get('/location/filter-pb', { params: { building, part } })
}

// ==============================
// Payment schedule
// ==============================

const getPaymentSchedules = (params = {}) => {
    return axiosInstance.get('/payment-schedules', { params })
}

const setManualPaymentSchedulePaid = (claimSlug, paymentScheduleId) => {
    return axiosInstance.post(`/claims/${claimSlug}/manual-payment-by-schedule`, { paymentScheduleId })
}

// ==============================
// Dashboard stats (statistics)
// ==============================

const getStats = (startDate, endDate) => {
    return axiosInstance.post('/stats/my', { startDate, endDate })
}

const getBankBics = () => {
    return axiosInstance.get('/users/bank-bics')
}

const getKBEs = () => {
    return axiosInstance.get('/users/kbes')
}

// ==============================
// Promocode
// ==============================

const redeemPromocode = (code) => {
    return axiosInstance.post('/promocodes/redeem', { code })
}

// ==============================
// Tours
// ==============================

const startTour = (tourName) => {
    return axiosInstance.post('/tours/start', { tourName })
}

const completeTour = (tourName) => {
    return axiosInstance.post('/tours/upsert', { tourName })
}

const getTourRoutes = () => {
    return axiosInstance.get('/tours/routes')
}

// ==============================
// System notifications
// ==============================
const getAnnouncement = () => {
    return axiosInstance.get('/system-notifications/get-announcement')
}

// ==============================
// OSI
// ==============================
const getOsiBuildings = () => {
    return axiosInstance.get('/osi/buildings')
}

const getOsiBuilding = (buildingId = null) => {
    // return { data: 'asdf' }
    return axiosInstance.get('/osi/get-building', { params: { buildingId } })
}

const getOwnAddress = (buildingId = null) => {
    return axiosInstance.get('/osi/get-own-address', { params: { buildingId } })
}

const getOsiSettings = (buildingId = null) => {
    return axiosInstance.get('/osi/settings', { params: { buildingId } })
}

const getOsiDebtTypes = () => {
    return axiosInstance.get('/osi/debt-types')
}

const previewOsiDebtImport = (file, statementDate, buildingId = null) => {
    const formData = new FormData()
    formData.append('file', file)
    formData.append('statementDate', statementDate)
    if(buildingId) formData.append('buildingId', buildingId)

    return axiosInstance.post('/osi/debt-imports/preview', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
        timeout: 60000
    })
}

const commitOsiDebtImport = (importId, data = {}, buildingId = null) => {
    return axiosInstance.post(`/osi/debt-imports/${importId}/commit`, { ...data, buildingId }, { timeout: 60000 })
}

const getOsiDebtImportHistory = (buildingId = null) => {
    return axiosInstance.get('/osi/debt-imports', { params: { buildingId } })
}

const revertOsiDebtImport = (importId, data = {}, buildingId = null) => {
    return axiosInstance.post(`/osi/debt-imports/${importId}/revert`, { ...data, buildingId }, { timeout: 60000 })
}

const bindOsiUnboundRecipient = (recipientAddressId, buildingPartId, buildingId = null) => {
    return axiosInstance.patch(`/osi/unbound-recipients/${recipientAddressId}/bind`, { buildingPartId, buildingId })
}

const removeOsiUnboundRecipient = (recipientAddressId, buildingId = null) => {
    return axiosInstance.delete(`/osi/unbound-recipients/${recipientAddressId}`, { params: { buildingId } })
}

const previewOsiSettings = (settings) => {
    return axiosInstance.post('/osi/settings/preview', { settings })
}

const saveOsiSettings = (settings, buildingId = null) => {
    return axiosInstance.post('/osi/settings', { settings, buildingId })
}

const getOsiObjectDebt = (buildingPartId, buildingId = null) => {
    return axiosInstance.get(`/osi/debts/${buildingPartId}`, { params: { buildingId } })
}

const saveOsiObjectDebt = (debt, buildingId = null) => {
    return axiosInstance.post('/osi/debts', { ...debt, buildingId })
}

const updateOsiObjectDebtStatus = (debtId, status, buildingId = null) => {
    return axiosInstance.patch(`/osi/debts/${debtId}/status`, { status, buildingId })
}

const deleteOsiObjectDebt = (debtId, buildingId = null) => {
    return axiosInstance.delete(`/osi/debts/${debtId}`, { params: { buildingId } })
}

const generateOsiDebtNotice = (debtId, data = {}, buildingId = null) => {
    return axiosInstance.post(`/osi/debts/${debtId}/generate-notice`, { ...data, buildingId })
}


const generateOsiDebtNotices = (data = {}, buildingId = null) => {
    return axiosInstance.post('/osi/debts/generate-notices', { ...data, buildingId })
}

const getOsiManagementCompanies = (params = {}) => {
    return axiosInstance.get('/osi-management/companies', { params })
}

const createOsiManagementCompany = (data) => {
    return axiosInstance.post('/osi-management/companies', data)
}

const updateOsiManagementCompany = (id, data) => {
    return axiosInstance.patch(`/osi-management/companies/${id}`, data)
}

const deleteOsiManagementCompany = (id) => {
    return axiosInstance.delete(`/osi-management/companies/${id}`)
}

const createOsiManagementBuilding = (data) => {
    return axiosInstance.post('/osi-management/buildings', data)
}

const updateOsiManagementBuilding = (id, data) => {
    return axiosInstance.patch(`/osi-management/buildings/${id}`, data)
}

const deleteOsiManagementBuilding = (id) => {
    return axiosInstance.delete(`/osi-management/buildings/${id}`)
}

export default {
    addMessage,
    addStreet,
    checkClaims,
    claimPayment,
    otpApproveCode,
    otpSendCode,
    getAnnouncement,
    getBills,
    getCurrentBalance,
    getCurrentBonusBalance,
    getClaimCost,
    getClaimPreview,
    getContactsLimits,
    getCountries,
    getMessages,
    getRates,
    getReceipt,
    getRecipient,
    findRecipientByFields,
    linkRecipientAddress,
    getRegions,
    getLocalities,
    getNotificationMethods,
    getPaymentSchedules,
    getRecipentAddress,
    getStats,
    getStreets,
    getSubLocalities,
    getTransactionStatus,
    getUnreadClaim,
    getUnreadDebtNotice,
    checkNotices,
    checkNoticeStatuses,
    setNoticeSenderNotified,
    readNotice,
    deleteDebtNotice,
    checkAddressRecipient,
    addAddressVisibility,
    addRecipientRelation,
    checkOsiStatus,
    importDebtNotices,
    uploadDebtNotices,
    getNoticeSenderData,
    checkNoticesHome,
    getSenderData,
    addressRecipients,
    messageAction,
    getMessageDataForSign,
    doMessageAction,
    updateRecipientContact,
    readClaim,
    redeemPromocode,
    removeRecipentContact,
    replenishAccount,
    requestBill,
    sendClaimNotifications,
    setManualPaymentSchedulePaid,
    noticePayment,
    getBankBics,
    getKBEs,
    startTour,
    completeTour,
    getTourRoutes,
    getRkaAddresses,
    getCities,
    getRKAStreets,
    getRKABuildings,
    getRKABuildingParts,
    getRKAAddress,
    getAddressesNumber,
    getFirstAddress,
    findCityByKato,
    findStreetByName,
    saveMigratedAddress,
    setHouseByNumber,
    setAptByNumber,
    deleteAddress,
    archive,
    filterBdg,
    getAllCities,
    getAllRKAStreets,
    getAllRKABuildings,
    getAllRKABuildingParts,
    filterPb,
    getOsiBuildings,
    getOsiBuilding,
    getOwnAddress,
    getOsiSettings,
    getOsiDebtTypes,
    previewOsiDebtImport,
    commitOsiDebtImport,
    getOsiDebtImportHistory,
    revertOsiDebtImport,
    bindOsiUnboundRecipient,
    removeOsiUnboundRecipient,
    previewOsiSettings,
    saveOsiSettings,
    getOsiObjectDebt,
    saveOsiObjectDebt,
    updateOsiObjectDebtStatus,
    deleteOsiObjectDebt,
    generateOsiDebtNotice,
    generateOsiDebtNotices,
    getOsiManagementCompanies,
    createOsiManagementCompany,
    updateOsiManagementCompany,
    deleteOsiManagementCompany,
    createOsiManagementBuilding,
    updateOsiManagementBuilding,
    deleteOsiManagementBuilding,
}
